import {
  CreateButton,
  Datagrid,
  FilterButton,
  List,
  TextField,
  TextInput,
  TopToolbar,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';

/**
 * The managed blog-category vocabulary list (D-cms-blog). Cloned from the storeTags
 * vocabulary (key / label / description): the editorial grouping a post references by
 * the stable `key`. EDITORIAL grouping (news, guides, brand-spotlight), DISTINCT from
 * the merchant product categories (conflation rejected).
 *
 * REFERENCE DATA, not switcher-scoped: categories are platform-wide editorial keys, so
 * there is no region filter here. Create and edit only, there is NO delete, because a
 * category a post references must not vanish (a retire flag is the additive, not built).
 * `q` matches the key / label / description. Authoring gates cms:author.
 */

const categoryFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
];

const BlogCategoryListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  const canAuthor = hasAccess(
    { role, accessibleRegions },
    { capability: 'cms:author' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canAuthor && <CreateButton label="New category" />}
    </TopToolbar>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const BlogCategoryListView = () => (
  <List
    resource="blogCategories"
    title="Blog categories"
    filters={categoryFilters}
    actions={<BlogCategoryListActions />}
    sort={{ field: 'label', order: 'ASC' }}
    perPage={50}
    exporter={false}
  >
    {/* Clicking a row opens the category edit. No bulk actions, no delete — categories
        are managed (create / edit), never destroyed. */}
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <TextField source="key" label="Key" />
      <TextField source="label" label="Label" />
      <TextField source="description" label="Description" />
    </Datagrid>
  </List>
);

/** Gated entry point. ROLE-gated to editor and super_admin. */
export const BlogCategoryList = () => (
  <RequireAccess roles={['editor', 'super_admin']}>
    <BlogCategoryListView />
  </RequireAccess>
);
